import { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import type { Contact } from '@/lib/contactsStore';
import { isValidName, isValidPhone, isValidEmail, sanitizeName, NAME_ERROR, PHONE_ERROR, EMAIL_ERROR } from '@/lib/validators';
import { PhoneInput } from '@/components/ui/phone-input';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  open: boolean;
  contact: Contact | null;
  onClose: () => void;
  onSave: (id: string, patch: Partial<Contact>) => void;
}

const CONTACT_TYPES = ['lay', 'solicitor', 'expert', 'witness', 'company'];
const KYC_OPTIONS = ['Valid', 'Expiring', '\u2014'];

const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const inputCls = 'h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100';
const selectCls = inputCls;

export function EditContactModal({ open, contact, onClose, onSave }: Props) {
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [org, setOrg] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [altPhone, setAltPhone] = useState('');
  const [kyc, setKyc] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && contact) {
      setName(contact.name);
      setType(contact.type);
      setOrg(contact.org);
      setEmail(contact.email);
      setPhone(contact.phone);
      setAltPhone(contact.mobilePhone ?? '');
      setKyc(contact.kyc);
      setTagsText(contact.tags.join(', '));
      setNotes(contact.notes);
    }
  }, [open, contact]);

  // Validate the name as a person's name unless this is a company contact
  // (company contacts use the name field for the organisation name).
  const nameError = type !== 'company' && name.trim() && !isValidName(name) ? NAME_ERROR : '';
  const emailError = email.trim() && !isValidEmail(email) ? EMAIL_ERROR : '';
  // Validate a phone only once the user has CHANGED it. A number already stored may predate
  // the current rule (the per-country lengths added 2026-10-06 made some older values invalid).
  // Flagging an untouched field trapped the user: `handleSave` refuses on any field error, so
  // they could not edit the notes on a legacy contact without first rewriting its phone number.
  // `handleSave` only sends a field that changed, so an untouched value never reaches the API.
  const phoneChanged = phone !== (contact?.phone ?? '');
  const altPhoneChanged = altPhone !== (contact?.mobilePhone ?? '');
  const phoneError = phoneChanged && phone.trim() && !isValidPhone(phone) ? PHONE_ERROR : '';
  const altPhoneError = altPhoneChanged && altPhone.trim() && !isValidPhone(altPhone) ? PHONE_ERROR : '';
  const hasFieldError = !!(nameError || emailError || phoneError || altPhoneError);

  if (!open || !contact) return null;

  const handleSave = () => {
    if (hasFieldError) {
      toast.error('Please correct the highlighted fields');
      return;
    }
    const patch: Partial<Contact> = {};

    if (name !== contact.name) patch.name = name;
    if (type !== contact.type) patch.type = type;
    if (org !== contact.org) patch.org = org;
    if (email !== contact.email) patch.email = email;
    if (phone !== contact.phone) patch.phone = phone;
    if (altPhone !== (contact.mobilePhone ?? '')) patch.mobilePhone = altPhone;
    if (kyc !== contact.kyc) patch.kyc = kyc;
    if (notes !== contact.notes) patch.notes = notes;

    const parsedTags = tagsText
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const tagsChanged =
      parsedTags.length !== contact.tags.length ||
      parsedTags.some((t, i) => t !== contact.tags[i]);
    if (tagsChanged) patch.tags = parsedTags;

    onSave(contact.id, patch);
    toast.success('Contact updated');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="flex w-full max-w-lg flex-col shadow-pop">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">Edit contact</h2>
          <button
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <CardContent className="max-h-[70vh] space-y-4 overflow-y-auto p-6">
          {/* Name */}
          <div>
            <label className={labelCls}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(type === 'company' ? e.target.value : sanitizeName(e.target.value))}
              maxLength={200}
              className={inputCls}
            />
            {nameError && <p className="mt-1 text-xs text-danger">{nameError}</p>}
          </div>

          {/* Type */}
          <div>
            <label className={labelCls}>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
              {CONTACT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Organisation */}
          <div>
            <label className={labelCls}>Organisation</label>
            <input
              type="text"
              value={org}
              onChange={(e) => setOrg(e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Email */}
          <div>
            <label className={labelCls}>Email</label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={255}
              className={inputCls}
            />
            {emailError && <p className="mt-1 text-xs text-danger">{emailError}</p>}
          </div>

          {/* Phone (primary) */}
          <div>
            <label className={labelCls}>Phone</label>
            <PhoneInput value={phone} onChange={setPhone} error={phoneError || undefined} />
          </div>

          {/* Alternate number */}
          <div>
            <label className={labelCls}>Alternate number</label>
            <PhoneInput value={altPhone} onChange={setAltPhone} error={altPhoneError || undefined} />
          </div>

          {/* KYC status */}
          <div>
            <label className={labelCls}>KYC status</label>
            <select value={kyc} onChange={(e) => setKyc(e.target.value)} className={selectCls}>
              {KYC_OPTIONS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div>
            <label className={labelCls}>Tags</label>
            <input
              type="text"
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              placeholder="e.g. Criminal, Repeat instructor"
              className={inputCls}
            />
            <p className="mt-1 text-xs text-muted-foreground">Separate with commas</p>
          </div>

          {/* Notes */}
          <div>
            <label className={labelCls}>Notes</label>
            <ExpandableTextarea
              label="Notes"
              rows={4}
              value={notes}
              onChange={setNotes}
            />
          </div>
        </CardContent>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSave}>
            Save changes
          </Button>
        </div>
      </Card>
      </div>
    </div>
  );
}
